/**
 * A porta do avaliador de fórmula — o que o resto do mundo importa.
 *
 * Tudo aqui dentro é **puro**: sem DOM, sem BlockSuite, sem `Date.now()` implícito. É a
 * decisão D-D do Discovery, e ela não é estética — é o que permite rodar a suíte inteira do
 * avaliador nesta estação, em milissegundos, sem build do Affine e sem navegador.
 *
 * A regra 8.48.1 do `CLAUDE.md` registra o preço de não ter isso: quatro defeitos que
 * passaram por 1047 testes verdes e morreram em dez minutos de campo, todos dependentes do
 * comportamento do outro lado. Um avaliador aritmético **não** tem outro lado — então tudo
 * o que puder ser provado aqui deve ser provado aqui, e o campo fica para o que só o campo
 * pega (render, edição, reatividade real).
 */

export { ErroDeFormula, erro, rotuloCurto, type CodigoDeErro } from './erros';
export { tokenizar, type Token, type TipoDeToken } from './tokenizer';
export {
  propriedadesReferenciadas, paraTexto, type No, type OperadorBinario,
} from './ast';
export { analisar, type CatalogoDeColunas } from './parser';
export {
  VAZIO, ehVazio, normalizar, comoNumero, comoTexto, comoBooleano, comparar, type Valor,
} from './valores';
export { FUNCOES, chamar, type ContextoDeFuncoes } from './funcoes';
export {
  formatar, lerNumero, FORMATOS, LOCALE_PADRAO, MOEDA_PADRAO,
  type FormatoDeSaida, type OpcoesDeFormato,
} from './formato';
export { avaliar, detectarCiclo, type ContextoDeAvaliacao } from './eval';

import { analisar, type CatalogoDeColunas } from './parser';
import { avaliar, type ContextoDeAvaliacao } from './eval';
import { formatar, type FormatoDeSaida, type OpcoesDeFormato } from './formato';
import { ErroDeFormula, rotuloCurto } from './erros';
import { comoTexto, ehVazio, type Valor } from './valores';

/** O que a célula precisa mostrar — valor OU erro, nunca os dois, nunca nenhum. */
export type Resultado =
  | { ok: true; valor: Valor; texto: string }
  | { ok: false; erro: ErroDeFormula; rotulo: string };

/**
 * O caminho completo: texto → árvore → valor → texto formatado.
 *
 * Existe para o teste e para quem quer o resultado de uma vez. O caminho de PRODUÇÃO separa
 * `analisar` (uma vez, quando a fórmula muda) de `avaliar` (uma vez por linha, reativo) —
 * reanalisar por linha jogaria fora o parser a cada render de tabela grande.
 */
export function calcular(
  expressao: string,
  catalogo: CatalogoDeColunas,
  contexto: ContextoDeAvaliacao,
  formato: FormatoDeSaida = 'numero',
  opcoes: OpcoesDeFormato = {}
): Resultado {
  try {
    const arvore = analisar(expressao, catalogo);
    const valor = avaliar(arvore, contexto);
    return { ok: true, valor, texto: comoTextoFormatado(valor, formato, opcoes) };
  } catch (e) {
    const err = e instanceof ErroDeFormula
      ? e
      : new ErroDeFormula('sintaxe', e instanceof Error ? e.message : String(e));
    return { ok: false, erro: err, rotulo: rotuloCurto(err) };
  }
}

/** Valor → o que aparece na célula. Vazio é vazio: **não** é "0" nem "—". */
export function comoTextoFormatado(
  valor: Valor,
  formato: FormatoDeSaida,
  opcoes: OpcoesDeFormato = {}
): string {
  if (ehVazio(valor)) return '';
  if (formato === 'texto') return comoTexto(valor);
  if (formato === 'booleano') return valor ? 'sim' : 'não';
  if (typeof valor === 'boolean') return valor ? 'sim' : 'não';
  if (typeof valor === 'string') return valor;
  return formatar(valor, formato, opcoes);
}
